'use client'

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react'
import Image from 'next/image'

import type { TopoFlowHandle } from '@/components/topo-flow'

interface HeroCyclerProps {
  phrases: string[]
  artwork?: Record<string, string>
  holdMs?: number
  topoFlowRef?: RefObject<TopoFlowHandle | null>
  onCycle?: (index: number) => void
}

type Phase = 'hold' | 'dropout' | 'rebuild'

/** Deterministic scatter values per word index so they stay stable across renders */
const SCATTER = [
  { x: '5px', r: '2.5deg' },
  { x: '-6px', r: '-1.8deg' },
  { x: '3px', r: '3.2deg' },
  { x: '-4px', r: '-2.1deg' },
  { x: '7px', r: '1.4deg' },
  { x: '-3px', r: '-3.5deg' },
  { x: '4px', r: '2.8deg' },
  { x: '-5px', r: '-1.2deg' },
]

const OUT_DURATION = 400
const IN_DURATION = 500

/** The other phrases in a random order, so each one gets the same share of screen time. */
function shuffledOthers(count: number, current: number) {
  const bag = Array.from({ length: count }, (_, i) => i).filter((i) => i !== current)
  for (let i = bag.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[bag[i], bag[j]] = [bag[j], bag[i]]
  }
  return bag
}

export function HeroCycler({
  phrases,
  artwork,
  holdMs = 2400,
  topoFlowRef,
  onCycle,
}: HeroCyclerProps) {
  const [phase, setPhase] = useState<Phase>('hold')
  const [displayIndex, setDisplayIndex] = useState(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const visibleRef = useRef(true)
  const containerRef = useRef<HTMLSpanElement>(null)
  const indexRef = useRef(0)
  const bagRef = useRef<number[]>([])
  const onCycleRef = useRef(onCycle)
  const topoFlowRefRef = useRef(topoFlowRef)

  useEffect(() => {
    onCycleRef.current = onCycle
  }, [onCycle])

  useEffect(() => {
    topoFlowRefRef.current = topoFlowRef
  }, [topoFlowRef])

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const scheduleRef = useRef<() => void>(() => {})

  useEffect(() => {
    function schedule() {
      timerRef.current = setTimeout(() => {
        if (!visibleRef.current) return
        setPhase('dropout')
        topoFlowRefRef.current?.current?.triggerRipple(OUT_DURATION + 100)

        timerRef.current = setTimeout(() => {
          if (!visibleRef.current) return
          if (!bagRef.current.length) {
            bagRef.current = shuffledOthers(phrases.length, indexRef.current)
          }
          const next = bagRef.current.pop() ?? 0
          indexRef.current = next
          setDisplayIndex(next)
          onCycleRef.current?.(next)
          setPhase('rebuild')

          timerRef.current = setTimeout(() => {
            if (!visibleRef.current) return
            setPhase('hold')
            schedule()
          }, IN_DURATION)
        }, OUT_DURATION)
      }, holdMs)
    }

    scheduleRef.current = schedule
    schedule()
    return clearTimer
  }, [holdMs, phrases.length, clearTimer])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        const wasVisible = visibleRef.current
        visibleRef.current = entry.isIntersecting

        if (!wasVisible && entry.isIntersecting) {
          // Restart animation when scrolling back into view
          clearTimer()
          setPhase('hold')
          scheduleRef.current()
        }

        if (wasVisible && !entry.isIntersecting) {
          clearTimer()
        }
      },
      { threshold: 0.1 }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [clearTimer])

  const phraseArtwork = artwork?.[phrases[displayIndex]]
  const words = useMemo(() => phrases[displayIndex].split(' '), [phrases, displayIndex])

  return (
    <span ref={containerRef} aria-live="polite" aria-atomic="true" className="block max-w-full">
      {phraseArtwork ? (
        <span className="hero-halloween-type flex" data-phase={phase}>
          <Image
            src={phraseArtwork}
            width={1000}
            height={249}
            alt={phrases[displayIndex]}
            className="block h-[2.5em] w-auto max-w-full object-contain object-left sm:h-[1em]"
            unoptimized
          />
        </span>
      ) : (
        words.map((word, i) => {
          const scatter = SCATTER[i % SCATTER.length]
          const wordCount = words.length
          const phaseClass =
            phase === 'dropout'
              ? 'hero-word hero-word-out'
              : phase === 'rebuild'
                ? 'hero-word hero-word-in'
                : 'hero-word hero-word-hold'

          const style: CSSProperties & Record<string, string | number> = {
            '--word-i': i,
            '--word-i-rev': wordCount - 1 - i,
            '--scatter-x': scatter.x,
            '--scatter-r': scatter.r,
          }

          return (
            <span key={`${displayIndex}-${i}`} className={phaseClass} style={style}>
              {word}
              {i < wordCount - 1 ? '\u00A0' : ''}
            </span>
          )
        })
      )}
    </span>
  )
}
